/**
 * SyncStatusChip — small persistent indicator showing pending offline writes.
 *
 * Only renders when there's something to show. Lives in the app shell
 * header so it's always visible regardless of which page you're on.
 *
 * Three visible states:
 *   • 🟢 nothing queued + online   → returns null (don't bother the user)
 *   • ⏳ items queued + online      → "Syncing N changes…"
 *   • 📵 items queued + offline    → "Offline · N pending"
 *
 * Tapping the chip opens a tiny drawer listing each item with retry/discard.
 */
import { useEffect, useState } from "react";
import { listPending, subscribeQueueChanged, discardItem } from "../../lib/offlineQueue/queue";
import { drainQueueNow } from "../../lib/offlineQueue/drain";

export default function SyncStatusChip() {
  const [items, setItems] = useState([]);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Refresh queue state on mount + whenever the queue changes
  // (queue.js fires a notify after every write).
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const all = await listPending();
        if (!cancelled) setItems(all);
      } catch { /* ignore */ }
    };
    refresh();
    const unsub = subscribeQueueChanged(refresh);
    // Also refresh on visibility change — covers iOS waking the tab.
    const onVis = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", onVis);
    // Online/offline events for the status text.
    const goOnline  = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      cancelled = true;
      unsub();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online",  goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const total = items.length;
  if (total === 0 && online) return null;  // happy path → invisible

  const failed = items.filter(i => i.status === "failed").length;
  const pending = total - failed;

  return (
    <>
      <button
        type="button"
        onClick={() => setDrawerOpen(true)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors ${
          !online
            ? "bg-rose-100 border border-rose-300 text-rose-800"
            : failed > 0
              ? "bg-amber-100 border border-amber-300 text-amber-900"
              : "bg-blue-100 border border-blue-300 text-blue-800"
        }`}
        data-testid="sync-status-chip"
        title={!online ? "Offline — changes will sync when you reconnect" : "Pending sync"}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${!online ? "bg-rose-500" : failed > 0 ? "bg-amber-500" : "bg-blue-500 animate-pulse"}`} />
        {!online && (<>📵 Offline · {total} pending</>)}
        {online && failed > 0 && (<>⚠ {failed} need retry</>)}
        {online && failed === 0 && pending > 0 && (<>⏳ Syncing {pending}</>)}
      </button>

      {drawerOpen && (
        <div className="fixed inset-0 z-[200] bg-black/40 flex items-end sm:items-center sm:justify-center" onClick={() => setDrawerOpen(false)}>
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="sync-status-drawer"
          >
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="text-base font-bold text-slate-900">Pending changes</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {!online ? "You're offline — these will send when you reconnect." : "Auto-syncing now…"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="text-slate-400 hover:text-slate-900 text-2xl leading-none px-2"
                aria-label="Close"
                data-testid="sync-drawer-close"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <p className="p-8 text-center text-sm text-slate-500">Nothing pending.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {items.map((it) => (
                    <li key={it.id} className="px-5 py-3 flex items-start gap-3" data-testid={`sync-item-${it.id}`}>
                      <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        it.status === "failed" ? "bg-amber-500" :
                        it.status === "syncing" ? "bg-blue-500 animate-pulse" :
                        "bg-slate-400"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 break-words">{it.label || `${it.method} ${it.url}`}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {it.status === "failed" ? `Failed: ${it.last_error || "unknown"}` :
                           it.status === "syncing" ? "Syncing…" :
                           `Queued ${new Date(it.enqueued_at).toLocaleTimeString()}`}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => discardItem(it.id)}
                        className="text-[11px] font-semibold text-slate-500 hover:text-rose-600 px-2 py-1"
                        data-testid={`sync-item-discard-${it.id}`}
                      >
                        Discard
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {items.length > 0 && online && (
              <div className="px-5 py-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => drainQueueNow()}
                  className="w-full px-3 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
                  data-testid="sync-drawer-retry-all"
                >
                  Retry now
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
